import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, UserCircle, Shield, Edit } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const { user: currentUser } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'moderator'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Nie udało się pobrać użytkowników');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        // Update role only
        await axios.put(`${API_URL}/users/${editingUser.id}`, { role: formData.role });
        toast.success('Rola użytkownika zaktualizowana');
      } else {
        // Create new user
        await axios.post(`${API_URL}/auth/register`, formData);
        toast.success('Użytkownik dodany');
      }
      
      setOpen(false);
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd podczas zapisywania użytkownika');
    }
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '', // Don't populate password
      role: user.role
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tego użytkownika?')) return;
    
    try {
      await axios.delete(`${API_URL}/users/${id}`);
      toast.success('Użytkownik usunięty');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nie udało się usunąć użytkownika');
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      role: 'moderator'
    });
  };

  return (
    <Layout>
      <div data-testid="users-container" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Użytkownicy</h1>
            <p className="text-zinc-400">Zarządzaj użytkownikami systemu</p>
          </div>
          <Dialog open={open} onOpenChange={(val) => {
            setOpen(val);
            if (!val) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-button" className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Dodaj użytkownika
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
              <DialogHeader>
                <DialogTitle>{editingUser ? 'Edytuj użytkownika' : 'Nowy użytkownik'}</DialogTitle>
                <DialogDescription className="text-zinc-400">
                  {editingUser ? 'Zmień rolę użytkownika' : 'Dodaj nowego użytkownika do systemu'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required={!editingUser}
                    disabled={!!editingUser}
                    className="bg-zinc-800 border-zinc-700 disabled:opacity-50"
                  />
                </div>
                {!editingUser && (
                  <div>
                    <Label htmlFor="password">Hasło</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="role">Rola</Label>
                  <Select value={formData.role} onValueChange={(val) => setFormData({...formData, role: val})}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="moderator">Moderator</SelectItem>
                      <SelectItem value="admin">Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500 mt-2">
                    Moderator: Zarządzanie treściami i harmonogramem<br />
                    Admin: Pełne uprawnienia (użytkownicy, ekrany)
                  </p>
                </div>
                <Button type="submit" className="w-full btn-primary">
                  {editingUser ? 'Zapisz zmiany' : 'Dodaj użytkownika'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {users.map((user) => (
            <Card key={user.id} data-testid={`user-card-${user.id}`} className="bg-zinc-900/80 backdrop-blur-sm border-zinc-800">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${
                      user.role === 'admin' ? 'bg-red-500/10' : 'bg-blue-500/10'
                    }`}>
                      {user.role === 'admin' ? (
                        <Shield className="w-5 h-5 text-red-400" />
                      ) : (
                        <UserCircle className="w-5 h-5 text-blue-400" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-white text-lg">{user.email}</CardTitle>
                      <CardDescription className="text-zinc-400 capitalize mt-1">
                        {user.role === 'admin' ? 'Administrator' : 'Moderator'}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-zinc-400 mb-4">
                  <p>Utworzono: {new Date(user.created_at).toLocaleDateString('pl-PL')}</p>
                  <p className="text-xs text-zinc-500 font-mono">ID: {user.id}</p>
                </div>
                {user.id !== currentUser?.id && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openEdit(user)}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-blue-900 text-blue-400 hover:bg-blue-900/20"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edytuj
                    </Button>
                    <Button
                      onClick={() => handleDelete(user.id)}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-red-900 text-red-400 hover:bg-red-900/20"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Usuń
                    </Button>
                  </div>
                )}
                {user.id === currentUser?.id && (
                  <div className="text-center text-sm text-zinc-500 py-2">
                    To Ty
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {users.length === 0 && (
          <Card className="bg-zinc-900/80 backdrop-blur-sm border-zinc-800">
            <CardContent className="py-12 text-center">
              <p className="text-zinc-500">Brak użytkowników w systemie</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}